import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  FormHelperText,
} from '@mui/material';

const defaultForm = {
  period_name: '',
  start_time: '',
  end_time: '',
};

const PeriodForm = ({
  open,
  onClose,
  onSubmit,
  initialData,
  loading = false,
  title = 'Add Period',
}) => {
  const [formData, setFormData] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        period_name: initialData.period_name || '',
        start_time: (initialData.start_time || '').slice(0, 5),
        end_time: (initialData.end_time || '').slice(0, 5),
      });
    } else {
      setFormData(defaultForm);
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const validationErrors = {};
    if (!formData.period_name.trim()) {
      validationErrors.period_name = 'Period name is required';
    }
    if (!formData.start_time) {
      validationErrors.start_time = 'Start time is required';
    }
    if (!formData.end_time) {
      validationErrors.end_time = 'End time is required';
    }

    if (!validationErrors.start_time && !validationErrors.end_time) {
      const start = formData.start_time;
      const end = formData.end_time;
      if (start >= end) {
        validationErrors.end_time = 'End time must be later than start time';
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      period_name: formData.period_name.trim(),
      start_time: formData.start_time.length === 5 ? `${formData.start_time}:00` : formData.start_time,
      end_time: formData.end_time.length === 5 ? `${formData.end_time}:00` : formData.end_time,
    };
    if (initialData?.id) {
      payload.id = initialData.id;
    }
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Period Name"
                value={formData.period_name}
                onChange={handleChange('period_name')}
                error={Boolean(errors.period_name)}
                helperText={errors.period_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.start_time}
                onChange={handleChange('start_time')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                error={Boolean(errors.start_time)}
                helperText={errors.start_time}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.end_time}
                onChange={handleChange('end_time')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                error={Boolean(errors.end_time)}
                helperText={errors.end_time}
                required
              />
            </Grid>
            {errors.form && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.form}</FormHelperText>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {initialData?.id ? 'Update Period' : 'Create Period'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeriodForm;
